/**
 * Map Boundaries Plugin for Search Results Map Variant
 *
 * Manages the lifecycle of the market area KML boundary layer and coordinates
 * map positioning using backend-suggested coordinate bounds.
 *
 * Responsibilities:
 * - Listen for 'onNewLocationBoundaries' custom events emitted by the fetch epic
 * - Manage the market area KML layer lifecycle (create, persist, remove)
 * - Position the map using backend-suggested bounds as a baseline
 * - Extend backend bounds with actual KML bounds when the KML loads successfully
 * - Silently fall back to backend bounds when KML fails to load
 * - Clean up layer references and subscriptions on stop
 *
 * Design principles:
 * - All business/orchestration logic lives exclusively in this plugin layer.
 * - All Google Maps interactions go through context.services.mapFacade (the facade).
 * - Boundary data flows via the mapBoundariesUpdated$ Subject (observables.js) — no Redux, no DOM events.
 * - Uses switchMap to cancel stale in-flight KML load operations when the URL changes.
 * - Consecutive identical URLs are deduplicated by distinctUntilChanged upstream.
 *
 * @module searchResultsMap/plugins/mapBoundaries.plugin
 */

import {
  combineLatest, distinctUntilChanged, filter, from, map, Observable, of, skip, startWith, switchMap, tap, EMPTY
} from 'rxjs';
import { selectLocationType, LocationType } from 'features/searchResultsFacets';
import { mapBoundariesUpdated$ } from '../redux/observables';

export default function createMapBoundariesPlugin(mapVisible$) {
  const state = {
    started: false,
    mapFacade: null,
    store$: null,
    getState: null,
    subscriptions: [],
    mapEventUnsubscribers: [],
    pendingInteractionGuardUnsubscribe: null,
    userHasInteracted: false,
    hasHandledFirstLoad: false,
    lastProcessedBoundaries: null,
  };

  function onUserInteraction() {
    state.userHasInteracted = true;
  }

  /**
   * Arms (or re-arms) a one-shot bounds_changed listener that marks the user as
   * having interacted with the map.
   *
   * Cancels any previously registered pending guard before creating a new one so
   * that location-type changes never stack up duplicate listeners. The listener
   * clears its own reference when it fires, so stop() can skip it if it has
   * already executed.
   */
  function armInteractionGuard() {
    if (state.pendingInteractionGuardUnsubscribe) {
      state.pendingInteractionGuardUnsubscribe();
      state.pendingInteractionGuardUnsubscribe = null;
    }
    state.pendingInteractionGuardUnsubscribe = state.mapFacade.onceMapEvent('bounds_changed', () => {
      state.pendingInteractionGuardUnsubscribe = null;
      onUserInteraction();
    });
  }

  /**
   * Fits the map to the provided backend bounds.
   * Used both as the baseline and as the silent fallback on KML failure.
   *
   * @param {{ minLatitude: number, minLongitude: number, maxLatitude: number, maxLongitude: number }|null} bounds
   */
  function applyBackendBounds(bounds) {
    if (bounds) {
      state.mapFacade.fitBounds(bounds);
    }
  }

  /**
   * Removes the current KML layer and resets the tracked URL.
   */
  function clearCurrentLayer() {
    state.mapFacade.removeKmlLayer();
  }

  /**
   * Loads a new KML layer for the given boundary URL and orchestrates map positioning.
   *
   * Flow:
   * 1. Capture a camera snapshot before the async KML load begins.
   * 2. Create the KML layer via the facade (previous layer is removed inside createKmlLayer)
   * 3. Wait for the KML to settle (success or failure) via onKmlSettled
   * 4. If the camera has moved since step 1 (user panned/zoomed), skip fitBounds entirely.
   * 5. On KML success with valid bounds: fit the map to the KML bounds exclusively.
   * 6. On KML failure: silently fall back to backend bounds only.
   *
   * Using switchMap in the parent subscription ensures that if a new boundary URL arrives
   * before the current KML settles, this observable is cancelled and a new one starts.
   *
   * @returns {import('rxjs').Observable}
   */
  function loadKmlAndPosition(boundaryUrl, backendBounds) {
    const { onKmlSettled } = state.mapFacade.createKmlLayer(boundaryUrl);

    return from(onKmlSettled).pipe(
      tap(({ success, kmlBounds }) => {
        const isFirstLoad = !state.hasHandledFirstLoad;
        state.hasHandledFirstLoad = true;

        if (isFirstLoad && state.userHasInteracted) {
          return;
        }

        if (success && kmlBounds) {
          state.mapFacade.fitBounds(kmlBounds);
        } else {
          applyBackendBounds(backendBounds);
        }
      }),
    );
  }

  /**
   * Resets boundary-handling flags whenever the location type changes (SPA navigation).
   *
   * Without this reset, flags carried over from a State SRP visit prevent the correct
   * boundary positioning on the subsequent non-state location:
   *
   *  - `hasHandledFirstLoad = true` causes the `!hasHandledFirstLoad` guard in
   *    `subscribeToMapBoundaries` to skip `applyBackendBounds` for locations that
   *    have no KML boundary URL, leaving the map at the wrong zoom level.
   *  - `userHasInteracted = true` (from the state map's fitBounds call) is harmless
   *    on its own now, but resetting it restores the "skip fitBounds if user already
   *    panned" protection for the new location type.
   *
   * Called before `subscribeToMapBoundaries` so the flags are clear before the new
   * location's first boundary response arrives.
   */
  function subscribeToLocationTypeReset() {
    const locationTypeReset = state.store$.pipe(
      map(selectLocationType),
      distinctUntilChanged(),
      skip(1), // skip initial emission — flags are already false at plugin start
    ).subscribe(() => {
      state.hasHandledFirstLoad = false;
      state.userHasInteracted = false;
      state.lastProcessedBoundaries = null;

      // Re-arm the interaction guard so user pan/zoom during the new location
      // type's KML load is detected correctly. The previous guard may have
      // already fired during the initial fitBounds, so we must register a
      // fresh one-shot listener here.
      armInteractionGuard();
    });
    state.subscriptions.push(locationTypeReset);
  }

  /**
   * Sets up the subscription to mapBoundariesUpdated$ that drives the KML lifecycle.
   *
   * - Reacts only when the boundary URL changes (via distinctUntilChanged on the URL).
   * - Uses switchMap to cancel any in-flight KML load when a new URL arrives.
   * - Skips reload when the URL matches the currently loaded layer (persistence).
   * - When mapVisible$ is provided, defers processing until the map is visible and
   *   replays the latest boundaries once visibility is restored.
   */
  function subscribeToMapBoundaries() {
    const boundaries$ = mapBoundariesUpdated$.pipe(
      startWith(null)
    );

    const mapReady$ = mapVisible$.pipe(
      switchMap((isVisible) => {
        if (!isVisible) {
          return of(false);
        }

        return new Observable((observer) => {
          const unsubscribe = state.mapFacade.onceMapEvent('idle', () => {
            observer.next(true);
            observer.complete();
          });
          return unsubscribe;
        });
      })
    );

    const source$ = combineLatest({
      boundaries: boundaries$,
      isVisible: mapReady$,
    }).pipe(
      filter(({ boundaries, isVisible }) => Boolean(isVisible && boundaries)),
      map(({ boundaries }) => boundaries)
    );

    state.subscriptions.push(
      source$.pipe(
        switchMap((mapBoundaries) => {
          if (mapBoundaries === state.lastProcessedBoundaries && state.userHasInteracted) {
            return EMPTY;
          }

          state.lastProcessedBoundaries = mapBoundaries;

          const boundaryUrl = mapBoundaries?.boundaryUrl;
          const backendBounds = mapBoundaries?.bounds;
          clearCurrentLayer();

          if (!boundaryUrl) {
            if (!state.hasHandledFirstLoad) {
              state.hasHandledFirstLoad = true;
              if (selectLocationType(state.getState()) !== LocationType.RectangularArea.toString()) {
                applyBackendBounds(backendBounds);
              }
            }

            return EMPTY;
          }

          return loadKmlAndPosition(boundaryUrl, backendBounds);
        }),
      ).subscribe()
    );
  }

  return {
    name: 'map-boundaries',

    async init(context) {
      context.state.mapBoundaries = {
        started: false,
      };
    },

    async start(context) {
      if (state.started) {
        return;
      }

      state.mapFacade = context.services.mapFacade;
      if (!state.mapFacade) {
        return;
      }

      state.store$ = context.services.store$;
      if (!state.store$) {
        return;
      }

      state.getState = context.services.getState;
      if (typeof state.getState !== 'function') {
        return;
      }

      armInteractionGuard();

      subscribeToLocationTypeReset();
      subscribeToMapBoundaries();

      state.started = true;
      context.state.mapBoundaries = {
        started: true,
      };
    },

    async stop(context) {
      state.subscriptions.forEach((subscription) => subscription?.unsubscribe?.());
      state.subscriptions = [];

      state.mapEventUnsubscribers.forEach((unsubscribe) => unsubscribe?.());
      state.mapEventUnsubscribers = [];

      if (state.pendingInteractionGuardUnsubscribe) {
        state.pendingInteractionGuardUnsubscribe();
        state.pendingInteractionGuardUnsubscribe = null;
      }

      state.mapFacade?.removeKmlLayer?.();

      state.started = false;
      state.mapFacade = null;
      state.store$ = null;
      state.getState = null;
      state.userHasInteracted = false;
      state.hasHandledFirstLoad = false;
      state.lastProcessedBoundaries = null;
      state.pendingInteractionGuardUnsubscribe = null;

      delete context.state.mapBoundaries;
    },
  };
}
